import React from 'react';
import GenericSelectBox from '../generic/GenericSelectBox';
import ValueSelector from '../elementSelector/selectors/ValueSelector';
import { lensPath, set, merge, clone, path } from 'ramda';
import { conditionTypes, pageConditionsBoilerplate, systemValues } from '../../constants';
import { Button } from 'react-bootstrap';
import '../../../css/page/page-conditions.less';
import translations from '../../translations';

const CONDITIONAL_VALUES = [
    { value: 'EQUAL', text: 'is equal to' },
    { value: 'NOT_EQUAL', text: 'is not equal to' },
    { value: 'IS_EMPTY', text: 'is empty' },
    { value: 'IS_NOT_EMPTY', text: 'is not empty' },
];

/**
 * @param {Component} initialComponent The component to render just above the scalar page condition tool
 * @param {Array} components Array of objects representing page components currently in model
 * @param {Object} metaData Response from element/page endpoint
 * @param {Function} onApply Callback to parent component for making update call to api
 * @param {Function} showValidationError Callback to parent component to show there is a validation error
 * @param {Function} onCancel Callback to parent component for cancelling out of the creation process
 * @param {Object} selectedComponent The currently selected component that activates the condition
 * @description Component for creating a scalar page condition
 */
class ScalarPageCondition extends React.Component {

    constructor() {
        super();
        this.state = {
            metaData: null,
            rightRuleValue: null,
            criteria: '',
            operationComponent: '',
            metaDataType: null,
            select: '',

            // For the value picker
            selectedValue: {
                isOpen: false,
                selectedValueMeta: null,
            },
        };

        this.metaDataTypePath = lensPath([
            'pageConditions', 0,
            'pageOperations', 0,
            'assignment',
            'assignee',
            'metadataType',
        ]);
        this.pageOpAssigneeComponentPath = lensPath([
            'pageConditions', 0,
            'pageOperations', 0,
            'assignment',
            'assignee',
            'pageObjectReferenceId',
        ]);
        this.pageOpAssignorValueIdPath = lensPath([
            'pageConditions', 0,
            'pageOperations', 0,
            'assignment',
            'assignor',
            'valueElementToReferenceId',
            'id',
        ]);
        this.pageOpAssignorValueNamePath = lensPath([
            'pageConditions', 0,
            'pageOperations', 0,
            'assignment',
            'assignor',
            'valueElementToReferenceId',
            'developerName',
        ]);
        this.rightRuleValueIdPath = lensPath([
            'pageConditions', 0,
            'pageRules', 0,
            'right',
            'valueElementToReferenceId',
            'id',
        ]);
        this.rightRuleValueNamePath = lensPath([
            'pageConditions', 0,
            'pageRules', 0,
            'right',
            'valueElementToReferenceId',
            'developerName',
        ]);
        this.leftRuleCriteria = lensPath([
            'pageConditions', 0,
            'pageRules', 0,
            'criteriaType',
        ]);
        this.leftRuleComponentPath = lensPath([
            'pageConditions', 0,
            'pageRules', 0,
            'left',
            'pageObjectReferenceId',
        ]);
    }

    componentDidMount() {
        let propsToState = this.props.metaData;
        if (this.props.metaData.pageConditions === null) {
            propsToState = merge(this.props.metaData, { pageConditions: [] });
        }
        propsToState.pageConditions.unshift(pageConditionsBoilerplate);
        this.setState({ metaData: propsToState });
    }

    updateMetaData = (metaDataMods) => {
        let currentMetaData = this.state.metaData;
        metaDataMods.forEach((mod) => {
            const metaDataClone = clone(currentMetaData);
            currentMetaData = set(mod.lens, mod.value, metaDataClone);
        });
        return currentMetaData;
    }

    updateRightRuleValue = (e) => {
        this.setState({
            selectedValue: {
                ...this.state.selectedValue,
                isOpen: false,
                selectedValueMeta: e,
            },
            rightRuleValue: e,
        });
    }

    updateCriteria = (e) => {
        const { value } = e.target;
        const newState = { criteria: value };

        // This is to handle when a value has already been selected
        // but then the user decides to then change the criteria to empty
        if (value === 'IS_EMPTY' || value === 'IS_NOT_EMPTY') {
            newState.rightRuleValue = null;
        }
        this.setState(newState);
    }

    updateOperationComponent = (e) => {
        const { value } = e.target;
        this.setState({ operationComponent: value });
    }

    updateMetaDataType = (e) => {
        const { value } = e.target;
        this.setState({ metaDataType: value });
    }

    onApply = () => {
        if (
            this.state.operationComponent &&
            this.state.metaDataType &&
            this.state.criteria
        ) {
            // When not empty is selected we just want to set the $False system value
            // as the right value reference in the page rule
            let rightRuleValue = null;
            if (this.state.criteria === 'IS_NOT_EMPTY') {
                rightRuleValue = systemValues.FALSE;
            }

            if (this.state.criteria === 'IS_EMPTY') {
                rightRuleValue = systemValues.TRUE;
            }

            if (this.state.rightRuleValue && typeof this.state.rightRuleValue === 'object') {
                ({ rightRuleValue } = this.state);
            }

            // Not empty is not a valid criteria - this works by setting it to IS_EMPTY
            // and applying the logic on the line above
            const criteria = this.state.criteria === 'IS_NOT_EMPTY' ? 'IS_EMPTY' : this.state.criteria;

            const metaDataMods = [
                { lens: this.leftRuleComponentPath, value: this.props.selectedComponent.id },
                { lens: this.rightRuleValueIdPath, value: path(['id'], rightRuleValue) },
                { lens: this.rightRuleValueNamePath, value: path(['developerName'], rightRuleValue) },
                { lens: this.pageOpAssignorValueIdPath, value: systemValues.TRUE.id },
                { lens: this.pageOpAssignorValueNamePath, value: systemValues.TRUE.developerName },
                { lens: this.pageOpAssigneeComponentPath, value: this.state.operationComponent },
                { lens: this.metaDataTypePath, value: this.state.metaDataType },
                { lens: this.leftRuleCriteria, value: criteria },
            ];

            const modifiedMetaData = this.updateMetaData(metaDataMods);

            this.setState({ metaData: modifiedMetaData }, () => {
                this.props.onApply(this.state.metaData, rightRuleValue);
            });

        } else {
            this.props.showValidationError();
        }
    }

    togglePickerModal = (isOpen) => {
        this.setState({
            selectedValue: {
                ...this.state.selectedValue,
                isOpen,
            },
        });
    }

    render() {
        const rulePageComponents = [];

        // Do not show the value picker when empty criteria types are selected
        const showPicker = this.state.criteria !== 'IS_EMPTY' && this.state.criteria !== 'IS_NOT_EMPTY';

        this.props.components.forEach((component) => {
            rulePageComponents.push(<option key={component.developerName.value} value={component.id.value}>{component.developerName.value}</option>);
        });

        const metaTypes = conditionTypes.map(type => (
            <option key={type.key} value={type.value}>{type.key}</option>
        ));

        const conditionals = CONDITIONAL_VALUES.map(conditional => (
            <option key={conditional.value} value={conditional.value}>{conditional.text}</option>
        ));

        rulePageComponents.unshift(
            <option key="default" value="" disabled hidden>-- Please Select --</option>,
        );

        metaTypes.unshift(
            <option key="default" value="" disabled hidden>-- Please Select --</option>,
        );

        conditionals.unshift(
            <option key="default" value="" disabled hidden>-- Please Select --</option>,
        );

        return (
            <>
                <div className="overflow-y-auto">
                    {this.props.initialComponent}

                    <GenericSelectBox key="valueRef" required onFilter={this.updateCriteria} selected={this.state.select}>
                        {conditionals}
                    </GenericSelectBox>
                    {
                        showPicker ? (
                            <ValueSelector
                                selectValue={this.updateRightRuleValue}
                                preSelectedValue={this.state.selectedValue.selectedValueMeta}
                                toggleModal={this.togglePickerModal}
                                isOpen={this.state.selectedValue.isOpen}
                            />
                        ) : null
                    }
                    <div className="flex align-center form-inline margin-top">
                        <span className="align-label">then make</span>
                        <GenericSelectBox key="assigneeComponent" required onFilter={this.updateOperationComponent} selected={this.state.select}>
                            {rulePageComponents}
                        </GenericSelectBox>

                        <GenericSelectBox key="metaDataType" required onFilter={this.updateMetaDataType} selected={this.state.select}>
                            {metaTypes}
                        </GenericSelectBox>
                    </div>
                </div>
                <div className="page-config-footer">
                    <Button bsStyle="default" className="btn-outline" onClick={this.props.onCancel}>Cancel</Button>
                    <Button bsStyle="primary" onClick={this.onApply}>
                        {
                            translations.PAGE_COND_BASIC_save
                        }
                    </Button>
                </div>
            </>
        );
    }
}

export default ScalarPageCondition;
